import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { LISTINO, PIANI_INFO, MODULI, moduloIncluso, statoPiano, normalizzaPiano, preventivo, euro, canoneMensile } from "../utils/pianiNexiora";

// ─── Piani ────────────────────────────────────────────────────────────
// Etichette, prezzi e moduli arrivano da utils/pianiNexiora.js (fonte unica, listino Rev1).
// Le chiavi "feature" storiche usate dalle pagine sono mappate sui moduli del listino.
export const FEATURE_MODULO = {
  progetti: "commesse", chat: "base", campiDati: "commesse", dashboardBase: "base",
  centroClienti: "commesse", gestCommesse: "commesse",
  kpi: "economico", valoriEconomici: "economico", controlloEconomico: "economico",
  reportCommessa: "report",
  reportAvanzati: "report", api: "direzione", automazioni: "direzione", whiteLabel: "direzione",
};

const featuresDelPiano = (piano) =>
  Object.keys(FEATURE_MODULO).filter(k => moduloIncluso(FEATURE_MODULO[k], piano));

export const PLANS = {
  free: {
    label: "Base", color: PIANI_INFO.free.colore, price: "Incluso in ogni piano",
    limits: { utenti: 0, progetti: Infinity },
    features: featuresDelPiano("free"),
  },
  pro: {
    label: "Pro", color: PIANI_INFO.pro.colore,
    price: `da ${euro(canoneMensile(LISTINO.bloccoPosti, "pro"))}/mese fino a ${LISTINO.bloccoPosti} utenti`,
    limits: { utenti: Infinity, progetti: Infinity },
    features: featuresDelPiano("pro"),
  },
  gold: {
    label: "Gold", color: PIANI_INFO.gold.colore,
    price: `da ${euro(canoneMensile(LISTINO.bloccoPosti, "gold"))}/mese fino a ${LISTINO.bloccoPosti} utenti`,
    limits: { utenti: Infinity, progetti: Infinity },
    features: featuresDelPiano("gold"),
  },
};

const DESCR = {
  progetti: "Gestione completa progetti, sezioni, attività e sottoattività",
  chat: "Chat privata e di gruppo tra utenti",
  campiDati: "Campi personalizzati per sezione",
  dashboardBase: "Vista home con statistiche di base",
  centroClienti: "Anagrafica clienti centralizzata",
  gestCommesse: "Commesse collegate ai clienti",
  kpi: "Centro Operativo e indicatori di cliente e commessa",
  valoriEconomici: "Valore economico commesse, visibile solo agli admin",
  controlloEconomico: "Motore economico per commessa: ore, materiali, trasporti, vitto, redditività",
  reportCommessa: "Report periodici e di chiusura commessa in PDF",
  reportAvanzati: "Report avanzati", api: "Integrazioni", automazioni: "Automazioni", whiteLabel: "Personalizzazione del marchio",
};
const LABEL = {
  progetti:"Progetti e Attività", chat:"Chat", campiDati:"Campi dinamici", dashboardBase:"Dashboard base",
  centroClienti:"Centro Clienti", gestCommesse:"Gestione Commesse", kpi:"Indicatori (KPI)", valoriEconomici:"Valori economici",
  controlloEconomico:"Controllo Economico", reportCommessa:"Report Commessa", reportAvanzati:"Report avanzati",
  api:"API", automazioni:"Automazioni", whiteLabel:"White Label",
};
export const FEATURE_DEFINITIONS = Object.fromEntries(Object.keys(FEATURE_MODULO).map(k =>
  [k, { label: LABEL[k], plan: MODULI[FEATURE_MODULO[k]].piano, description: DESCR[k] }]));

export const PLAN_ORDER = { free: 0, pro: 1, gold: 2 };

const p5 = preventivo(1, "pro");
export const UPGRADE_MESSAGE = {
  reportCommessa: "Il Report Commessa è incluso nel piano Gold: report periodici e fascicoli di chiusura in PDF con tutti i dati già presenti in Nexiora.",
  utenti: `La prova è terminata: per abilitare utenti serve il piano Pro o Gold, da ${euro(p5.mensile)}/mese IVA esclusa fino a ${LISTINO.bloccoPosti} utenti.`,
  progetti: "Commesse illimitate in tutti i piani.",
};

// ─── Context ──────────────────────────────────────────────────────────
const FeaturesContext = createContext();

export function useFeatures() {
  return useContext(FeaturesContext);
}

export function FeaturesProvider({ children }) {
  const { organization, isPlatformOwner } = useAuth();

  const orgPlan = normalizzaPiano(organization?.plan);
  const orgFeatures = organization?.features || {};
  const stato = statoPiano(organization, { isPlatformOwner });

  const hasFeature = useMemo(() => (featureKey) => {
    if (isPlatformOwner) return true;
    if (orgFeatures[featureKey] === false) return false;   // disattivata a mano dall'organizzazione
    const modulo = FEATURE_MODULO[featureKey];
    if (!modulo) return false;
    return moduloIncluso(modulo, stato.pianoEffettivo);    // la prova include tutto
  }, [isPlatformOwner, orgFeatures, stato.pianoEffettivo]);

  const checkLimit = useMemo(() => (limitKey, currentCount) => {
    if (isPlatformOwner) return { allowed: true };
    if (limitKey !== "utenti") return { allowed: true };
    if (stato.pianoPagato || stato.inProva) return { allowed: true };
    // Rev2: senza piano e a prova finita non si abilitano utenti
    return { allowed: false, limit: 0, message: UPGRADE_MESSAGE.utenti };
  }, [isPlatformOwner, stato.pianoPagato, stato.inProva]);

  const planCoversFeature = (featureKey) => {
    if (isPlatformOwner) return true;
    return (PLANS[orgPlan]?.features || []).includes(featureKey);
  };

  const requiredPlanFor = (featureKey) => FEATURE_DEFINITIONS[featureKey]?.plan || "free";

  return (
    <FeaturesContext.Provider value={{
      hasFeature, checkLimit, planCoversFeature, requiredPlanFor,
      orgPlan, PLANS, FEATURE_DEFINITIONS, PLAN_ORDER,
    }}>
      {children}
    </FeaturesContext.Provider>
  );
}
